import { useState } from "react";
import { latestFeed, hotSpots } from "@/mocks/news";

const breakingLines = latestFeed.slice(0, 6);

export default function Hero() {
  const [index, setIndex] = useState(0);

  return (
    <section id="hero" className="relative overflow-hidden bg-background-50 pt-24">
      {/* 顶部通栏跑马灯 */}
      <div className="relative z-10 flex items-center overflow-hidden border-y border-primary-200 bg-primary-500 text-background-50">
        <div className="z-10 flex shrink-0 items-center gap-1.5 bg-primary-600 px-3 py-2 md:px-4">
          <span className="h-2 w-2 rounded-full bg-accent-300 animate-pulse-dot" />
          <span className="whitespace-nowrap text-xs font-bold md:text-sm">快讯滚动</span>
        </div>
        <div className="relative flex-1 overflow-hidden py-2">
          <div className="animate-marquee flex w-max items-center gap-10 whitespace-nowrap pr-10 text-sm md:text-[15px]">
            {[...breakingLines, ...breakingLines].map((n, i) => (
              <span key={n.id + i} className="inline-flex items-center gap-2">
                <i className="ri-flashlight-fill text-xs text-accent-300" />
                {n.title}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-8 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-12">
        {/* 左侧主推 */}
        <div className="lg:col-span-7">
          <div className="animate-rise animate-rise-1 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">
              <i className="ri-fire-fill" /> 全网最热
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              <i className="ri-radar-line" /> 刚刚发生 · 实时更新
            </span>
          </div>

          <h1 className="animate-rise animate-rise-2 mt-4 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl md:leading-[1.15]">
            全网最快吃瓜，
            <span className="text-primary-600">刚刚发生</span>的
            <br className="hidden md:block" />
            每一帧现场都不错过。
          </h1>
          <p className="animate-rise animate-rise-3 mt-5 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
            聚焦今日新瓜与最新进展，用克制而完整的时间线还原事件现场细节。
            不站队、不催热、不带节奏，只做你身边最快也最清醒的速报指南。
          </p>

          <div className="animate-rise animate-rise-4 mt-7 flex flex-wrap items-center gap-3">
            <a
              href="#latest"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-transform hover:bg-primary-600 hover:-translate-y-0.5 cursor-pointer"
            >
              <i className="ri-refresh-line" /> 马上追最新进展
            </a>
            <a
              href="#live"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-background-100 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-camera-lens-line" /> 进入现场直击
            </a>
          </div>

          <dl className="mt-8 grid max-w-md grid-cols-3 divide-x divide-background-200">
            {[
              ["12+", "每日速报时段"],
              ["08", "核心内容栏目"],
              ["1min", "平均追瓜时长"],
            ].map(([v, k]) => (
              <div key={k} className="px-4 first:pl-0">
                <dt className="font-heading text-2xl font-black text-foreground-950">{v}</dt>
                <dd className="mt-1 text-xs text-foreground-500">{k}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* 右侧焦点卡轮播 */}
        <div className="lg:col-span-5">
          <div className="relative overflow-hidden rounded-2xl border border-background-200 bg-background-100">
            <div className="relative h-[360px] w-full md:h-[440px]">
              <img
                src={hotSpots[index].image}
                alt={hotSpots[index].title}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/10 to-transparent" />
              <div className="absolute inset-x-0 top-0 flex items-center gap-2 p-4">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                  <span className="h-2 w-2 rounded-full bg-accent-300 animate-pulse-dot" /> 现场直击
                </span>
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5">
                <p className="text-xs font-medium text-accent-300">焦点追踪 · {hotSpots[index].place}</p>
                <h2 className="mt-2 font-heading text-xl font-bold leading-snug text-background-50 md:text-2xl">
                  {hotSpots[index].title}
                </h2>
                <p className="mt-2 line-clamp-2 text-xs leading-5 text-background-200/90 md:text-sm">
                  {hotSpots[index].summary}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-background-200/90">
                    <span className="inline-flex items-center gap-1"><i className="ri-eye-line" />{hotSpots[index].reads}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-thumb-up-line" />{hotSpots[index].up}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {hotSpots.map((s, i) => (
                      <button
                        key={s.id}
                        type="button"
                        aria-label={`查看第${i + 1}条现场`}
                        onClick={() => setIndex(i)}
                        className={
                          "h-1.5 rounded-full transition-all cursor-pointer " +
                          (i === index ? "w-6 bg-accent-400" : "w-1.5 bg-background-50/50")
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 下方悬浮信息条 */}
      <div className="border-t border-background-200 bg-background-50">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-5 md:grid-cols-3 md:px-6">
          {[
            ["ri-time-line", "刚刚发生", "事件第一现场的速递，快到你还没切屏"],
            ["ri-focus-3-line", "现场直击", "多信源交叉还原，还原每一处关键细节"],
            ["ri-bar-chart-box-line", "今日新瓜", "热度真实可查，帮你抓住今日讨论焦点"],
          ].map(([icon, t, d]) => (
            <div key={t as string} className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                <i className={(icon as string) + " text-lg"} />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-foreground-950">{t}</p>
                <p className="mt-0.5 text-xs leading-5 text-foreground-500">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}